'use client'

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useRouter } from '@/i18n/navigation'
import { getApiUrl } from '@/lib/api-url'
import { getApiErrorMessage } from '@/lib/auth-error'
import { getClientLocale } from '../lib/client-locale'
import { clearCachedPhoneHash } from '../lib/gtm'

interface User {
  id: string
  email?: string | null
  mobileNumber?: string | null
  name?: string | null
  firstName?: string | null
  lastName?: string | null
  role: string
  userType?: string | null
  emailVerified?: boolean
  profileImageUrl?: string | null
  pendingEmail?: string | null
}

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  loginWithOtp: (mobileNumber: string, otpCode: string, userType?: string) => Promise<void>
  logout: () => Promise<void>
  isAuthenticated: boolean
  /** Merge into session user + localStorage (e.g. after profile update). */
  mergeUserSession: (patch: Partial<User>) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const router = useRouter()

  useEffect(() => {
    // Check for stored user data
    const storedUser = localStorage.getItem('user')
    const token = localStorage.getItem('accessToken')
    
    if (storedUser && token) {
      try {
        setUser(JSON.parse(storedUser))
      } catch (e) {
        localStorage.removeItem('user')
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
      }
    }
    setLoading(false)
  }, [])

  const getLanguageHeader = () => getClientLocale()

  const login = async (email: string, password: string) => {
    const language = getLanguageHeader()
    const response = await fetch(`${getApiUrl()}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(language ? { 'x-language': language } : {}),
      },
      body: JSON.stringify({ email, password }),
    })

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(getApiErrorMessage(data, 'خطا در ورود'))
    }

    const data = await response.json()
    localStorage.setItem('accessToken', data.accessToken)
    localStorage.setItem('refreshToken', data.refreshToken)
    if (data.sessionToken) {
      localStorage.setItem('sessionToken', data.sessionToken)
    }
    localStorage.setItem('user', JSON.stringify(data.user))
    setUser(data.user)
  }

  const loginWithOtp = async (mobileNumber: string, otpCode: string, userType?: string) => {
    const language = getLanguageHeader()
    const response = await fetch(`${getApiUrl()}/auth/verify-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(language ? { 'x-language': language } : {}),
      },
      body: JSON.stringify({ mobileNumber, otpCode, userType }),
    })

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(getApiErrorMessage(data, 'خطا در تایید کد'))
    }

    const data = await response.json()
    if (data.accessToken) {
      localStorage.setItem('accessToken', data.accessToken)
    }
    if (data.refreshToken) {
      localStorage.setItem('refreshToken', data.refreshToken)
    }
    if (data.sessionToken) {
      localStorage.setItem('sessionToken', data.sessionToken)
    }
    localStorage.setItem('user', JSON.stringify(data.user))
    setUser(data.user)
  }

  const logout = async () => {
    const refresh = localStorage.getItem('refreshToken')
    if (refresh) {
      try {
        await fetch(`${getApiUrl()}/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: refresh }),
        })
      } catch {
        /* ignore */
      }
    }
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('sessionToken')
    localStorage.removeItem('user')
    // The GTM identity cache holds a hash of this person's phone number.
    clearCachedPhoneHash()
    setUser(null)
    if (typeof window !== 'undefined') {
      window.location.replace('/')
      return
    }
    router.push('/')
  }

  const mergeUserSession = (patch: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return prev
      const next = { ...prev, ...patch }
      localStorage.setItem('user', JSON.stringify(next))
      return next
    })
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        loginWithOtp,
        logout,
        isAuthenticated: !!user,
        mergeUserSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

